import {makeAutoObservable} from "mobx";
import axios from "axios";
import {getFolderStore} from "./FolderStore";
import {getStore, useStore} from "./index";

export class PageStore {
    store;
    /** @type {ResponsePage[]} */
    pages = [];
    /** @type {ResponsePage[]} */
    searchPages = [];
    /** @type {Folder[]} */
    pageFolders = [];
    /** @type {Folder[]} */
    searchFolders = [];

    /** @type {Record<string,number>} */
    pageCount = {
        ALL: 0, WORKING: 0, REQUEST_INSPECT: 0, REQUEST_RE_MODIFICATION: 0, CHECK_COMPLETE: 0, CONNECTED: 0
    };

    pageIndex = 1;
    pageLimit = 10;
    sort = '-updatedAt';
    status = 'ALL';
    searchType = "name";
    searchString = "";
    searchState = false;
    searchReset = false;
    /** @type {string[]} */
    breadCrumb = [];
    breadBookmark = false;

    searchTypeList = [
        {value: "name", label: "이름"},
        {value: "productNo", label: "상품번호"},
    ];

    orderSort = [
        {value: "-updatedAt", label: "최신 업데이트 순"},
        {value: "updatedAt", label: "오래된 업데이트 순"},
        {value: "name", label: "가나다순"},
        {value: "-createdAt", label: "최신 최초 작성 순"},
        {value: "createdAt", label: "오래된 최초 작성 순"},
    ];

    pageLength = [
        {value: 10, label: "10개씩 보기"},
        {value: 30, label: "30개씩 보기"},
        {value: 50, label: "50개씩 보기"},
        {value: 100, label: "100개씩 보기"},
    ];

    constructor(store) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * 디테일 리스트를 벗어날 때 페이지를 초기화 시키는 함수
     */
    clear() {
        this.pages = [];
        this.pageFolders = [];
        this.searchFolders = [];
        this.searchPages = [];
        this.pageCount = {
            ALL: 0, WORKING: 0, REQUEST_INSPECT: 0, REQUEST_RE_MODIFICATION: 0, CHECK_COMPLETE: 0, CONNECTED: 0
        };
        this.pageIndex = 1;
        this.pageLimit = 10;
        this.sort = '-updatedAt';
        this.status = 'ALL';
        this.breadCrumb = [];
        this.searchType = "name"
        this.searchString = "";
        this.searchState = false;
        this.searchReset = false;
    }

    /**
     * 검색 이후에 폴더를 클릭하여 새로운 경로로 진입할 때 검색기록을 지워주는 함수
     */
    clearSearch() {
        this.searchString = "";
        this.searchFolders = [];
        this.searchPages = [];
        this.searchReset = false;
        this.searchState = false;
    }

    /**
     * 첫번째 뎁스로 이동시 브레드 크럼 초기로 가는 함수
     */
    clearBreadBookMark() {
        this.breadCrumb = [];
    }

    /**
     * 디테일리스트에서 표시되는 폴더 + 파일의 개수를 구하는 함수
     * @return {number}
     */
    getContentLength() {
        // console.log(this.getPages().length, this.getPageFolders().length)
        return Math.min(this.getPages().length + this.getPageFolders().length, this.pageLimit);
    }

    /**
     * 서버로부터 받아온 페이지 정보를 세팅하는 함수
     * @param list
     */
    setPageList(list) {
        if (list) {
            this.pages = list;
        }
    }

    /**
     * 페이지의 status 별 카운트를 세팅하는 함수
     * @param {Array<{status:string,count:number}>} countList
     */
    setPageCount(countList) {
        if (countList) {
            this.pageCount = {
                ALL: 0, WORKING: 0, REQUEST_INSPECT: 0, REQUEST_RE_MODIFICATION: 0, CHECK_COMPLETE: 0, CONNECTED: 0
            };
            let allCount = 0;
            for (const count of countList) {
                allCount += count.count;
                this.pageCount[count.status] = count.count;
            }
            this.pageCount['ALL'] = allCount;
        }
    }

    /**
     * 페이지네이션에서 사용되는 인덱스 넘버를 세팅하는 함수
     * @param {number} index 인덱스 넘버
     */
    setPageIndex(index) {
        this.pageIndex = index;
    }

    /**
     * 한 페이지에 표현될 최대 개수를 세팅하는 함수
     * 기본값 20
     * @param {number} limit 최대 개수
     */
    setPageLimit(limit) {
        this.pageLimit = limit;
    }

    /**
     * 페이지의 정렬방식을 변경하는 함수
     * @param {string} sort 정렬 방식
     */
    setPageSort(sort) {
        this.sort = sort;
        this.fetchNotificationPageListOnly(getFolderStore().getCurrent('PAGE')).then();
    }

    /**
     * 리스트에 보여질 상태 필터링을 결정하는 함수
     * @param {string} status 상태값, enum:
     */
    setPageStoreStatus(status) {
        this.status = status;
        this.pageIndex = 1;
        this.fetchNotificationPageListOnly(getFolderStore().getCurrent('PAGE')).then();
    }

    /**
     * 페이지 리스트에 보여지는 폴더리스트를 세팅하는 함수
     * @param {Folder} folder 현재 폴더의 정보
     */
    setPageFolders(folder) {
        this.pageFolders = [];
        for (const item of folder.children) {
            this.pageFolders.push(item);
        }
        this.pageFolders.sort((a, b) => {
            switch (this.sort) {
                case 'updatedAt':
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case '-updatedAt':
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                case 'name':
                    return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
                case 'createdAt':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case '-createdAt':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                default:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
        });
    }

    /**
     * 검색 타입을 설정하는 함수
     * @param {string} searchType 검색 타입
     */
    setSearchType(searchType) {
        this.searchType = searchType;
    }

    /**
     * 검색 문자열을 설정하는 함수
     * @param {string} text 검색 문자열
     */
    setSearchString(text) {
        this.searchString = text;
    }

    /**
     * 검색한 결과인지 아닌지를 판단하기 위한 state 를 세팅하는 함수
     * @param {boolean} state
     */
    setSearchState(state) {
        this.searchState = state;
    }

    /**
     * 검색어 리셋 버튼에 관련한 state 를 세팅하는 함수
     * @param {boolean} state
     */
    setSearchReset(state) {
        this.searchReset = state;
    }

    /**
     * 페이지 리스트를 가져오는 함수
     * 페이지네이션에 따라 폴더 정보만 리턴 될 경우 빈 어레이를 리턴
     * @param {Folder} folder 현재 폴더의 정보
     * @param {number?} pageIndex 검색시 1페이지로 보내기 위한 넘버
     * @return {Promise<ResponsePage[]>}
     */
    async fetchNotificationPageList(folder, pageIndex) {
        const path = folder.path.split(`PAGE,`)[1] && folder.path.split(`PAGE,`)[1].slice(0, -1);
        let index = pageIndex ? pageIndex : this.pageIndex;
        let limit = this.pageLimit;
        let skip = 0;
        if (this.searchString.length > 0) {
            this.searchFolders = this.pageFolders.filter((x) => {
                if (x.name.indexOf(this.searchString) > -1) {
                    return Object.create(x)
                }
            })
            if (this.searchFolders.length >= index * limit) {
                return [];
            }
            skip = ((index - 1) * limit) - this.searchFolders.length;
        } else if (folder.children.length > 0 && folder.children.length >= index * limit) {
            return [];
        } else {
            skip = ((index - 1) * limit) - folder.children.length;
            limit = Math.min((index * limit) - folder.children.length, limit);
        }
        if (skip < 0) skip = 0;
        let url = path && path.length > 0 ? `/page/list?path=${path}&skip=${skip}&limit=${limit}&sort=${this.sort}` :
            `/page/list?skip=${skip}&limit=${limit}&sort=${this.sort}`;
        if (this.searchType && this.searchString.length > 0) {
            this.searchFolders = [];
            this.searchPages = [];
            url += `&searchType=${this.searchType}&searchString=${this.searchString}`;
        }
        if (this.status) {
            url += `&status=${this.status}`;
        }
        const res = await axios.get(url);
        return res.data.data.pages;
    }

    /**
     * 폴더의 경로가 변하지 않고 페이지네이션에 의해 호출될 때 카운트를 새로고침 하지 않고 페이지 리스트만 가져오기 위한 함수
     * @param {Folder} folder 현재 폴더의 정보
     * @return {Promise<void>}
     */
    async fetchNotificationPageListOnly(folder) {
        const pages = await this.fetchNotificationPageList(folder);
        this.setPageFolders(folder);
        if (this.searchType && this.searchString.length > 0) {
            this.searchPages = pages;
        } else {
            this.setPageList(pages);
        }
    }

    /**
     * 새로운 폴더 경로로 진입하거나 검색했을 때 페이지의 카운트와 리스트를 새로고침 해주는 함수
     * @param {Folder} folder 현재 폴더의 정보
     * @param {number?} pageIndex 검색시 1페이지로 보내기 위한 넘버
     * @return {Promise<void>}
     */
    async fetchNotificationPageListAndCount(folder, pageIndex) {
        const pageCount = await this.fetchPageCount(folder.path.split(`PAGE,`)[1] && folder.path.split(`PAGE,`)[1].slice(0, -1));
        const pages = await this.fetchNotificationPageList(folder, pageIndex);
        this.setPageFolders(folder);
        if (this.searchType && this.searchString.length > 0) {
            this.searchPages = pages;
        } else {
            this.setPageList(pages);
        }

        this.setPageCount(pageCount);
        if (pageIndex) {
            this.setPageIndex(pageIndex)
        }
    }

    /**
     * 서버로부터 페이지 카운트 정보를 가져오기 위한 함수
     * @param {string} path 폴더의 경로
     * @return {Promise<Record<string, number>|*>}
     */
    async fetchPageCount(path) { // 폴더 패스
        const queryList = [];
        if (path && path.length > 0) queryList.push(`path=${path}`);
        if (this.searchString.length > 0) {
            queryList.push(`searchType=${this.searchType}`);
            queryList.push(`searchString=${this.searchString}`);
        }
        const url = queryList.length > 0 ? `/page/count?${queryList.join('&')}` : `/page/count`
        const res = await axios.get(url);
        return res.data.data.pageCount;
    }

    /**
     * 페이지 리스트의 정렬을 변경하기 위한 함수
     * @param {string} sortValue 정렬 타입
     * @return {Promise<void>}
     */
    async changeSort(sortValue) {
        this.sort = sortValue;
        let moreData = {
            path: this.breadCrumb.join(','), // 경로
            searchType: this.searchType, // 검색 타입
            searchString: this.searchString,
            sort: this.sort, // 정렬 기준
        }
        // 반복문을 통해 value값이 빈값일경우 key값도 삭제
        for (let key in moreData) {
            if (moreData[key] === "") {
                delete moreData[key]
            }
        }
        let payloadString = Object.entries(moreData).map(e => e.join('=')).join('&');

        const res = await axios.get(`/page/list?${payloadString}`)
        this.setPageList(res.data.data.pages);
    }

    /**
     * 페이지 파일의 status 에 대응되는 한글을 리턴하는 함수
     * @param {PageInfo} item 페이지 정보
     * @return {string}
     */
    listPageStoreStatus = (item) => {
        switch (item.status) {
            case "WORKING" :
                return "작업중"
            case "REQUEST_INSPECT" :
                return "검수 요청"
            case "REQUEST_RE_MODIFICATION" :
                return "재수정 요청"
            case "CHECK_COMPLETE" :
                return "확인 완료"
            case "CONNECTED":
                return "연결중"
            default :
                return item.status
        }
    }

    /**
     * 디테일 리스트에서의 폴더 경로를 보여주는 breadCrumb 를 세팅하는 함수
     * @param entry
     * @param entryType
     */
    setBreadCrumb(entry, entryType) {
        entry = entry.folder ? entry.folder : entry;
        this.breadCrumb = getFolderStore().findParentFolders(entryType, entry);
        this.breadBookmark = getFolderStore().bookmarks[entryType].some((s) => s.folder && s.folder._id === entry._id);
    }

    /**
     * 페이지 화면에 보여지는 폴더 리스트를 리턴하는 함수
     * 필터 여부에 따라 검색 폴더 리스트나 기본 폴더 리스트 중에 하나를 리턴
     * @return {Folder[]}
     */
    getPageFolders() {
        return this.searchState ? this.searchFolders : this.pageFolders;
    }

    /**
     * 페이지 화면에 보여지는 페이지 리스트를 리턴하는 함수
     * 필터 여부에 따라 검색 페이지 리스트나 기본 페이지 리스트 중에 하나를 리턴
     * @return {ResponsePage[]}
     */
    getPages() {
        return this.searchState ? this.searchPages : this.pages;
    }
}

export const usePageStore = () => {
    return useStore().pageStore;
}

export const getPageStore = () => {
    return getStore().pageStore;
}
